/**
 * Dosage-calculation arithmetic for nursing-maths practice (coursework only; no drug
 * data, no safe-dose ranges, never for a real order).
 *
 * - Desired over have:  amount to give = (desired ÷ have) × quantity, after the desired
 *   dose and the dose on hand are put in the same unit.
 * - Weight-based:       dose = ordered dose per kg × body weight in kg; a per-day order is
 *   divided by the number of doses a day; pounds become kilograms by ÷ 2.2.
 * - Unit conversion:    multiply by a chain of conversion factors so the unwanted unit
 *   cancels (dimensional analysis).
 *
 * Conversion factors are the ones nursing-maths texts teach (for example Open RN, "Nursing
 * Skills", chapter 5 "Math Calculations"): 1 kg = 1,000 g, 1 g = 1,000 mg, 1 mg = 1,000 mcg,
 * 1 kg = 2.2 lb, 1 L = 1,000 mL, 1 tsp = 5 mL, 1 tbsp = 15 mL (3 tsp), 1 fl oz = 30 mL,
 * 1 cup = 240 mL. The 2.2, 30 and 240 are the teaching approximations, not exact values.
 */

export type UnitCategory = "mass" | "volume";

export interface UnitInfo {
  symbol: string;
  category: UnitCategory;
  /** Size in the category's base unit: milligrams for mass, millilitres for volume. */
  size: number;
}

export const POUNDS_PER_KILOGRAM = 2.2;

export const UNITS: readonly UnitInfo[] = [
  { symbol: "kg", category: "mass", size: 1_000_000 },
  { symbol: "g", category: "mass", size: 1000 },
  { symbol: "mg", category: "mass", size: 1 },
  { symbol: "mcg", category: "mass", size: 0.001 },
  { symbol: "lb", category: "mass", size: 1_000_000 / POUNDS_PER_KILOGRAM },
  { symbol: "L", category: "volume", size: 1000 },
  { symbol: "mL", category: "volume", size: 1 },
  { symbol: "tsp", category: "volume", size: 5 },
  { symbol: "tbsp", category: "volume", size: 15 },
  { symbol: "fl oz", category: "volume", size: 30 },
  { symbol: "cup", category: "volume", size: 240 },
];

export function unitInfo(symbol: string): UnitInfo | undefined {
  return UNITS.find((unit) => unit.symbol === symbol);
}

export interface Conversion {
  value: number;
  /** The factor multiplied in, expressed as "to-units per from-unit". */
  factor: number;
}

/** Convert between two units of one category; null if they are not comparable. */
export function convertUnit(
  value: number,
  from: string,
  to: string
): Conversion | null {
  const source = unitInfo(from);
  const target = unitInfo(to);
  if (!(source && target) || source.category !== target.category) {
    return null;
  }
  const factor = source.size / target.size;
  return { value: value * factor, factor };
}

/** Amount to give = (desired ÷ have) × quantity; the two doses must share a unit. */
export function desiredOverHave(
  desired: number,
  have: number,
  quantity: number
): number {
  return (desired / have) * quantity;
}

export function poundsToKilograms(pounds: number): number {
  return pounds / POUNDS_PER_KILOGRAM;
}

/** Dose from an order per kilogram and a body weight in kilograms. */
export function weightBasedDose(
  perKilogram: number,
  kilograms: number
): number {
  return perKilogram * kilograms;
}

export interface WeightBasedResult {
  kilograms: number;
  /** The whole day's dose when the order was per day; otherwise equal to `perDose`. */
  perDay: number | null;
  perDose: number;
}

export function weightBased(
  orderPerKg: number,
  weight: number,
  weightUnit: "kg" | "lb",
  basis: "dose" | "day",
  dosesPerDay: number
): WeightBasedResult {
  const kilograms = weightUnit === "lb" ? poundsToKilograms(weight) : weight;
  const total = weightBasedDose(orderPerKg, kilograms);
  return basis === "day"
    ? { kilograms, perDay: total, perDose: total / dosesPerDay }
    : { kilograms, perDay: null, perDose: total };
}

/** Round half up to a number of decimals. */
export function roundHalfUp(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return (
    Math.round((value + Number.EPSILON * Math.sign(value)) * factor) / factor
  );
}
